import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSpinner } from '@/hooks/useSpinner';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  Trophy,
  Medal,  
  Award,
  TrendingUp,
  CalendarIcon,
  Calendar as CalendarDays,
  CalendarCheck,
  Coins,
  ShoppingCart,
  Info,
  Filter,
  X,
  RefreshCw,
  CircleDollarSign
} from 'lucide-react';
import { saleService, type RankingUser, type RankingResponse } from '@/services/sale.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { configuracoesService } from '@/services/configuracoes.service';


interface TabProps {
  period: 'week' | 'month';
  type: 'value' | 'qntd';
  rankingData: RankingUser[];
  isFiltering: boolean;
  periodoInicio: Date | null;
  periodoFim: Date | null;
  selectedMonth?: number;
  selectedYear?: number;
  isFilteringMonth?: boolean;
  onPeriodFilter: (inicio: Date | null, fim: Date | null) => void;
  onMonthFilter: (month: number, year: number) => void;
  onClearFilter: () => void;
  onClearMonthFilter: () => void;
  showResultWeek: boolean;
  showResultMonth: boolean;
}

const RankingTab: React.FC<TabProps> = ({
  period, 
  type, 
  rankingData,
  isFiltering,
  periodoInicio,
  periodoFim,
  selectedMonth,
  selectedYear,
  isFilteringMonth,
  onPeriodFilter,
  onMonthFilter,
  onClearFilter,
  onClearMonthFilter,
  showResultWeek,
  showResultMonth
}) => {
  const { user } = useAuthContext();
  const [startDate, setStartDate] = useState<Date | undefined>(periodoInicio || undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(periodoFim || undefined);

  const months = [
    { value: 1, name: 'Janeiro' },
    { value: 2, name: 'Fevereiro' },
    { value: 3, name: 'Março' },
    { value: 4, name: 'Abril' },
    { value: 5, name: 'Maio' },
    { value: 6, name: 'Junho' },
    { value: 7, name: 'Julho' },
    { value: 8, name: 'Agosto' },
    { value: 9, name: 'Setembro' },
    { value: 10, name: 'Outubro' },
    { value: 11, name: 'Novembro' },
    { value: 12, name: 'Dezembro' }
  ];

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  const getPositionEmoji = (index: number) => {
    switch (index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return `${index + 1}º`;
    }
  };

  const getPositionIcon = (index: number) => {
    switch (index) {
      case 0: return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 1: return <Medal className="w-5 h-5 text-gray-400" />;
      case 2: return <Award className="w-5 h-5 text-orange-400" />;
      default: return <div className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">{index + 1}º</div>;
    }
  };

  const getPodiumStyle = (index: number) => {
    switch (index) {
      case 0: return "bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300";
      case 1: return "bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300";
      case 2: return "bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300";
      default: return "bg-card border-border";
    }
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  const handleApplyPeriodFilter = () => {
    if (startDate && endDate) {
      // Validate that start is Sunday and end is Saturday for weekly ranking
      if (period === 'week') {
        const isInicioDomingo = startDate.getDay() === 0;
        const isFimSabado = endDate.getDay() === 6;
        if (!isInicioDomingo || !isFimSabado) {
          alert('Para o ranking semanal, o período deve começar em um domingo e terminar em um sábado.');
          return;
        }
      }
      onPeriodFilter(startDate, endDate);
    }
  };

  const handleApplyMonthFilter = () => {
    if (selectedMonth && selectedYear) {
      onMonthFilter(selectedMonth, selectedYear);
    }
  };

  return (
    <div className="">
      {/* Period/Month Filters */}
      <div className="space-y-4">
        {period === 'week' && user?.role === 'admin' && (
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Filtrar por Período</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Data Início</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "PPP", { locale: ptBR }) : "Selecione a data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          disabled={(date) => !isWeekend(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Data Fim</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "PPP", { locale: ptBR }) : "Selecione a data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          disabled={(date) => !isWeekend(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleApplyPeriodFilter} size="sm">
                      <Filter className="w-4 h-4 mr-2" />
                      Filtrar
                    </Button>
                    {isFiltering && (
                      <Button onClick={onClearFilter} variant="outline" size="sm">
                        <X className="w-4 h-4 mr-2" />
                        Limpar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {period === 'month' && user?.role === 'admin' && (
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">Filtrar por Mês</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Ano</label>
                    <Select value={selectedYear?.toString()} onValueChange={(value) => selectedYear && onMonthFilter(selectedMonth || new Date().getMonth() + 1, parseInt(value))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o ano" />
                      </SelectTrigger>
                      <SelectContent>
                        {generateYears().map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Mês</label>
                    <Select value={selectedMonth?.toString()} onValueChange={(value) => selectedYear && onMonthFilter(parseInt(value), selectedYear)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o mês" />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(month => (
                          <SelectItem key={month.value} value={month.value.toString()}>{month.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleApplyMonthFilter} size="sm">
                      <Filter className="w-4 h-4 mr-2" />
                      Filtrar
                    </Button>
                    {isFilteringMonth && (
                      <Button onClick={onClearMonthFilter} variant="outline" size="sm">
                        <X className="w-4 h-4 mr-2" />
                        Limpar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      {/* Ranking Table */}
      <Card className='rounded-none border-none'>       
        <CardContent className="p-0 ">
          {rankingData.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum dado de ranking encontrado.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Revendedora</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rankingData.map((item, index) => (
                    <tr key={item.user.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-3">
                          <span className="text-xs">
                            {getPositionEmoji(index)}
                          </span>
                          <div className='max-w-40 truncate'>
                            <span className="font-medium text-xs w-full ">
                              {item.user.nome_razao_social}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="font-bold text-xs">
                          {type === 'value'
                            ? Number(item.total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : item.quantidadeVendas
                          }
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SaibaMaisDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <Button variant="outline" size="default" className='border-gray-200/80 text-muted-foreground'>
        <Info className="w-4 h-4 mr-2" />
        Saiba mais
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-80">
      <DialogHeader>
        <DialogTitle>Saiba mais sobre o ranking:</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 text-sm">
        <p>O ranking mensal é encerrado no <strong>último dia do mês</strong> às <strong>19 horas e 59 minutos</strong>.</p>
        <p>O resultado do ranking pode ser conferido até às <strong>21h</strong>.</p>
        <p>Contudo, o novo ranking já começa a valer a partir das <strong>20h</strong> também do último dia do mês.</p>
        <p>
          Exemplo: o ranking de <strong>janeiro</strong> se encerra às <strong>19h59 do dia 31</strong>. 
          Você pode conferir o resultado até as <strong>21h</strong>. As vendas realizadas a partir das <strong>20h</strong> já contam 
          para o ranking de <strong>fevereiro</strong>, que vai se encerrar às <strong>19h59</strong> do dia 30. E assim por diante!
        </p>
      </div>
       <DialogClose asChild>
        <Button type="button" variant="default">
          Entendido
        </Button>
      </DialogClose>
    </DialogContent>    
  </Dialog>
);

const RankingVendas: React.FC = () => {
  const { isLoading, showSpinner, hideSpinner } = useSpinner();
  const { toast } = useToast();
  const { user } = useAuthContext();

  // States
  const [rankingWeekValue, setRankingWeekValue] = useState<RankingUser[]>([]);
  const [rankingWeekQntd, setRankingWeekQntd] = useState<RankingUser[]>([]);
  const [rankingMonthValue, setRankingMonthValue] = useState<RankingUser[]>([]);
  const [rankingMonthQntd, setRankingMonthQntd] = useState<RankingUser[]>([]);

  // Configuration states
  const [rankingConfig, setRankingConfig] = useState({
    ranking_week_value: 0,
    ranking_week_qntd: 0,
    ranking_month_value: 0,
    ranking_month_qntd: 0,
    ranking: 0
  });

  // Filter states
  const [isFiltering, setIsFiltering] = useState(false);
  const [periodoInicio, setPeriodoInicio] = useState<Date | null>(null);
  const [periodoFim, setPeriodoFim] = useState<Date | null>(null);

  // Month filter states
  const [isFilteringMonth, setIsFilteringMonth] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Show result states
  const [showResultWeek, setShowResultWeek] = useState(false);
  const [showResultMonth, setShowResultMonth] = useState(false);

  // Weekly ranking dates (like Angular)
  const [inicioRankingSemanal, setInicioRankingSemanal] = useState<Date>(new Date());
  const [fimRankingSemanal, setFimRankingSemanal] = useState<Date>(new Date());

  const [activeTab, setActiveTab] = useState<'week' | 'month'>('week');
  const [activeSubTab, setActiveSubTab] = useState<'value' | 'qntd'>('value');

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    // If it is past 9pm on the last day of the month, increment month state by 1 using setSelectedMonth
    const today = new Date();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    if (today.getDate() === lastDayOfMonth.getDate() && today.getHours() >= 21) {
      setSelectedMonth(today.getMonth() + 2);
    }
    try {
      showSpinner('Aguarde', 'Carregando...');
      
      // Calculate week and month timing like Angular
      const today = new Date();
      const dayOfWeek = today.getDay();
      const hourShowResult = 20;

      // Saturday 8pm logic for showing week results
      if (dayOfWeek === 6 && today.getHours() === hourShowResult) {
        setShowResultWeek(true);
      }

      // Calculate weekly ranking dates (exact Angular logic)
      const lastSundayCopy = new Date(today);
      let inicioSemanal: Date;
      let fimSemanal: Date;

      // If Saturday 9pm+ (21h+), start new week
      if (dayOfWeek === 6 && today.getHours() >= (hourShowResult + 1)) {
        inicioSemanal = new Date();
      } else {
        inicioSemanal = new Date(lastSundayCopy.setDate(lastSundayCopy.getDate() - (dayOfWeek + 1)));
      }
      inicioSemanal.setHours(0, 0, 0, 0);

      const todayDate = new Date(today);
      const daysUntilNextSaturday = (6 - dayOfWeek + 7) % 7;

      // If Saturday 9pm+, calculate next Saturday
      if (dayOfWeek === 6 && today.getHours() >= (hourShowResult + 1)) {
        fimSemanal = new Date(todayDate.setDate(todayDate.getDate() + 7));
      } else {
        fimSemanal = new Date(todayDate.setDate(todayDate.getDate() + daysUntilNextSaturday));
      }
      fimSemanal.setHours(0, 0, 0, 0);

      setInicioRankingSemanal(inicioSemanal);
      setFimRankingSemanal(fimSemanal);

      // Check if should show month results (last day of month 8pm)
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      if (today.getDate() === lastDayOfMonth.getDate() && today.getHours() === hourShowResult) {
        setShowResultMonth(true);
      }

      // Load configurations from API like Angular
      const response = await configuracoesService.getAllConfigs();    
      setRankingConfig({
        ranking_week_value: response.find((c: any) => c.name === 'ranking_week_value')?.value === '1' ? 1 : 0,
        ranking_week_qntd: response.find((c: any) => c.name === 'ranking_week_qntd')?.value === '1' ? 1 : 0,
        ranking_month_value: response.find((c: any) => c.name === 'ranking_month_value')?.value === '1' ? 1 : 0,
        ranking_month_qntd: response.find((c: any) => c.name === 'ranking_month_qntd')?.value === '1' ? 1 : 0,
        ranking: response.find((c: any) => c.name === 'ranking')?.value === '1' ? 1 : 0,
      });
      // Only load ranking data if ranking is enabled
      if (response.ranking === 0) {
        toast({
          title: 'Aviso',
          description: 'O ranking está desabilitado no momento.',
          variant: 'default'
        });
        return;
      }

      // Load ranking data after configs are loaded
      await loadRankingData(response);

    } catch (error) {
      console.error('Erro ao inicializar:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados do ranking.',
        variant: 'destructive'
      });
    } finally {
      hideSpinner();
    }
  };

  const loadRankingData = async (response?: any) => {
    const configs = {
      ranking_week_value: response.find((c: any) => c.name === 'ranking_week_value')?.value === '1' ? 1 : 0,
      ranking_week_qntd: response.find((c: any) => c.name === 'ranking_week_qntd')?.value === '1' ? 1 : 0,
      ranking_month_value: response.find((c: any) => c.name === 'ranking_month_value')?.value === '1' ? 1 : 0,
      ranking_month_qntd: response.find((c: any) => c.name === 'ranking_month_qntd')?.value === '1' ? 1 : 0,
      ranking: response.find((c: any) => c.name === 'ranking')?.value === '1' ? 1 : 0,
    }
    try {
      const currentConfig = configs || rankingConfig;
      
      // Determine if we should show results based on timing (like Angular)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const hourShowResult = 20;
      let periodoInicio = undefined;
      let periodoFim = undefined;
      
      // Saturday 8pm logic for showing results
      if (dayOfWeek === 6 && today.getHours() === hourShowResult) {
        periodoInicio = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        periodoInicio.setHours(20, 0, 0);
        periodoFim = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        periodoFim.setHours(19, 59, 59);
      }

      // Load week rankings only if enabled
      if (currentConfig.ranking_week_value) {
        const weekValueData = await saleService.getRanking('week', 'value', periodoInicio, periodoFim);
        setRankingWeekValue(weekValueData.rankingValue || []);
      }

      if (currentConfig.ranking_week_qntd) {
        const weekQntdData = await saleService.getRanking('week', 'qntd', periodoInicio, periodoFim);
        setRankingWeekQntd(weekQntdData.rankingQntd || []);
      }

      // Load month rankings only if enabled
      if (currentConfig.ranking_month_value) {
        const monthValueData = await saleService.getRanking('month', 'value');
        setRankingMonthValue(monthValueData.rankingValue || []);
      }

      if (currentConfig.ranking_month_qntd) {
        const monthQntdData = await saleService.getRanking('month', 'qntd');
        setRankingMonthQntd(monthQntdData.rankingQntd || []);
      }

    } catch (error) {
      console.error('Erro ao carregar dados do ranking:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados do ranking.',
        variant: 'destructive'
      });
    }
  };

  const handlePeriodFilter = async (inicio: Date | null, fim: Date | null) => {
    if (!inicio || !fim) return;

    try {
      setIsFiltering(true);
      showSpinner('Aguarde', 'Aplicando filtro...');

      const adjustedFim = new Date(fim);
      adjustedFim.setHours(23, 59, 59, 999);

      const weekValueData = await saleService.getRanking('week', 'value', inicio, adjustedFim);
      const weekQntdData = await saleService.getRanking('week', 'qntd', inicio, adjustedFim);

      setRankingWeekValue(weekValueData.rankingValue || []);
      setRankingWeekQntd(weekQntdData.rankingQntd || []);

      setPeriodoInicio(inicio);
      setPeriodoFim(adjustedFim);

    } catch (error) {
      console.error('Erro ao aplicar filtro:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao aplicar filtro por período.',
        variant: 'destructive'
      });
    } finally {
      hideSpinner();
    }
  };

  const handleMonthFilter = async (month: number, year: number) => {
    try {
      setIsFilteringMonth(true);
      showSpinner('Aguarde', 'Aplicando filtro...');

      // Start: last day of previous month at 23:00
      const dataInicio = new Date(year, month - 1, 0);
      dataInicio.setHours(20, 0, 0, 0);

      // End: last day of selected month at 22:59:59
      const dataFim = new Date(year, month, 0);
      dataFim.setHours(19, 59, 59, 999);

      const monthValueData = await saleService.getRanking('month', 'value', dataInicio, dataFim);
      const monthQntdData = await saleService.getRanking('month', 'qntd', dataInicio, dataFim);

      setRankingMonthValue(monthValueData.rankingValue || []);
      setRankingMonthQntd(monthQntdData.rankingQntd || []);

      setSelectedMonth(month);
      setSelectedYear(year);

    } catch (error) {
      console.error('Erro ao aplicar filtro mensal:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao aplicar filtro mensal.',
        variant: 'destructive'
      });
    } finally {
      hideSpinner();
    }
  };

  const handleClearFilter = async () => {
    try {
      setIsFiltering(false);
      setPeriodoInicio(null);
      setPeriodoFim(null);
      showSpinner('Aguarde', 'Recarregando dados...');
      // Reload original data without filters
      await loadRankingData();
    } catch (error) {
      console.error('Erro ao limpar filtro:', error);
    } finally {
      hideSpinner();
    }
  };

  const handleClearMonthFilter = async () => {
    try {
      setIsFilteringMonth(false);
      setSelectedMonth(new Date().getMonth() + 1);
      setSelectedYear(new Date().getFullYear());
      showSpinner('Aguarde', 'Recarregando dados...');
      // Reload original data without filters
      await loadRankingData();
    } catch (error) {
      console.error('Erro ao limpar filtro mensal:', error);
    } finally {
      hideSpinner();
    }
  };

  const getCurrentRankingData = () => {
    if (activeTab === 'week') {
      return activeSubTab === 'value' ? rankingWeekValue : rankingWeekQntd;
    } else {
      return activeSubTab === 'value' ? rankingMonthValue : rankingMonthQntd;
    }
  };

  const hasWeeklyRanking = rankingConfig.ranking_week_value === 1 || rankingConfig.ranking_week_qntd === 1;
  const hasMonthlyRanking = rankingConfig.ranking_month_value === 1 || rankingConfig.ranking_month_qntd === 1;
  
  // Set default active tab based on available rankings
  useEffect(() => {
    if (hasWeeklyRanking && !hasMonthlyRanking) {
      setActiveTab('week');
    } else if (!hasWeeklyRanking && hasMonthlyRanking) {
      setActiveTab('month');
    } else if (hasWeeklyRanking) {
      setActiveTab('week'); // Default to week if both available
    }
  }, [hasWeeklyRanking, hasMonthlyRanking]);

  // Set default active sub-tab based on available ranking types
  useEffect(() => {
    if (activeTab === 'week') {
      if (rankingConfig.ranking_week_value === 1 && rankingConfig.ranking_week_qntd !== 1) {
        setActiveSubTab('value');
      } else if (rankingConfig.ranking_week_qntd === 1 && rankingConfig.ranking_week_value !== 1) {
        setActiveSubTab('qntd');
      } else if (rankingConfig.ranking_week_value === 1) {
        setActiveSubTab('value'); // Default to value if both available
      }
    } else if (activeTab === 'month') {
      if (rankingConfig.ranking_month_value === 1 && rankingConfig.ranking_month_qntd !== 1) {
        setActiveSubTab('value');
      } else if (rankingConfig.ranking_month_qntd === 1 && rankingConfig.ranking_month_value !== 1) {
        setActiveSubTab('qntd');
      } else if (rankingConfig.ranking_month_value === 1) {
        setActiveSubTab('value'); // Default to value if both available
      }
    }
  }, [activeTab, rankingConfig]);

  return (
    <div className="p-4 space-y-6">
      <LoadingSpinner isLoading={isLoading} />

      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 mb-2">
            <Award className="w-6 h-6 text-primary" />
            Ranking de Vendas
          </CardTitle>
          <SaibaMaisDialog />
          {/* Results Display */}
          {(activeTab === 'week' && showResultWeek) && (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-green-800 font-medium">🎉 Resultado do Ranking Semanal Disponível</p>
              </CardContent>
            </Card>
          )}

          {(activeTab === 'month' && showResultMonth) && (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-green-800 font-medium">🎉 Resultado do Ranking Mensal Disponível</p>
              </CardContent>
            </Card>
          )}
        </CardHeader>
      </Card>
      
      {/* TODO: User Position Info Card */}

      {/* Main Tab Navigation */}
      <Card>
        <CardContent className="p-0">
          <div className="flex border-b">
            {hasWeeklyRanking && (
              <button
                onClick={() => setActiveTab('week')}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors",
                  activeTab === 'week' 
                    ? "border-b-2 border-primary text-primary bg-primary/5" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <CalendarDays className="w-4 h-4" />
                Esta Semana
              </button>
            )}
            {hasMonthlyRanking && (
              <button
                onClick={() => setActiveTab('month')}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors",
                  activeTab === 'month' 
                    ? "border-b-2 border-primary text-primary bg-primary/5" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <CalendarCheck className="w-4 h-4" />
                Este Mês - {selectedMonth.toString().padStart(2, '0')}/{selectedYear}
              </button>
            )}
          </div>

          {/* Sub Tab Navigation */}
          <div className="p-4">
            <Card>
              <CardContent className="p-0">
                <div className="flex border-b">
                  {((activeTab === 'week' && rankingConfig.ranking_week_value === 1) || 
                    (activeTab === 'month' && rankingConfig.ranking_month_value === 1)) && (
                    <button
                      onClick={() => setActiveSubTab('value')}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors",
                        activeSubTab === 'value' 
                          ? "border-b-2 border-primary text-primary bg-primary/5" 
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <CircleDollarSign className="w-4 h-4" />
                      Por Valor
                    </button>
                  )}
                  {((activeTab === 'week' && rankingConfig.ranking_week_qntd === 1) || 
                    (activeTab === 'month' && rankingConfig.ranking_month_qntd === 1)) && (
                    <button
                      onClick={() => setActiveSubTab('qntd')}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors",
                        activeSubTab === 'qntd' 
                          ? "border-b-2 border-primary text-primary bg-primary/5" 
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Por <span className="hidden sm:inline">Quantidade</span><span className="sm:hidden">Qntd</span>
                    </button>
                  )}
                </div>

                <div className="px-0 py-0">
                  <RankingTab
                    period={activeTab}
                    type={activeSubTab}
                    rankingData={getCurrentRankingData()}
                    isFiltering={isFiltering}
                    periodoInicio={periodoInicio}
                    periodoFim={periodoFim}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    isFilteringMonth={isFilteringMonth}
                    onPeriodFilter={handlePeriodFilter}
                    onMonthFilter={handleMonthFilter}
                    onClearFilter={handleClearFilter}
                    onClearMonthFilter={handleClearMonthFilter}
                    showResultWeek={showResultWeek}
                    showResultMonth={showResultMonth}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RankingVendas;